import { css } from 'lit';

export const whiteNodeStyles = css`
  :host {
    --socket-size: 20px;
    --socket-margin: 6px;
    --node-inline-padding: 10px;

    display: block;
    background: var(--color-sidebar-surface, #ffffff);
    border: 1px solid var(--color-app-border, rgba(15, 23, 42, 0.1));
    border-radius: 8px;
    cursor: pointer;
    box-sizing: border-box;
    padding: 0;
    position: relative;
    box-shadow:
      0 8px 18px rgba(15, 23, 42, 0.08),
      0 1px 2px rgba(15, 23, 42, 0.08);
    transition:
      background 0.16s ease,
      border-color 0.16s ease,
      box-shadow 0.16s ease;
    z-index: 1;
    overflow: visible;
  }

  :host::before {
    background: transparent;
    border-radius: 0 999px 999px 0;
    bottom: 10px;
    content: '';
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 10px;
    transition: opacity 0.16s ease;
    width: 3px;
  }

  :host(:hover) {
    background: var(--color-sidebar-surface-hover, #f8fafc);
    border-color: rgba(63, 99, 243, 0.32);
    box-shadow:
      0 10px 22px rgba(15, 23, 42, 0.1),
      0 1px 2px rgba(15, 23, 42, 0.08);
  }

  :host(.status-running) {
    border-color: rgba(63, 99, 243, 0.34);
  }

  :host(.status-running)::before {
    background: var(--color-sidebar-selected-border, #3f63f3);
    opacity: 0.86;
  }

  :host(.status-completed) {
    border-color: rgba(21, 128, 61, 0.34);
  }

  :host(.status-completed)::before {
    background: var(--color-success-text, #15803d);
    opacity: 0.86;
  }

  :host(.status-failed),
  :host(.error) {
    border-color: rgba(220, 38, 38, 0.55);
  }

  :host(.status-failed)::before,
  :host(.error)::before {
    background: var(--color-error-text, #dc2626);
    opacity: 0.94;
  }

  :host(.selected) {
    outline: none;
    background: linear-gradient(
      180deg,
      var(--color-sidebar-selected-bg, #eef4ff) 0%,
      var(--color-sidebar-surface, #ffffff) 72%
    );
    border-color: var(--color-sidebar-selected-border, #3f63f3);
    box-shadow:
      0 0 0 1px var(--color-sidebar-selected-border, #3f63f3),
      0 0 0 4px rgba(63, 99, 243, 0.14),
      0 10px 22px rgba(15, 23, 42, 0.1);
  }

  :host(.selected)::before {
    opacity: 0;
  }

  :host(.disabled) {
    filter: saturate(0.82);
    opacity: 0.46;
  }

  .node-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    box-sizing: border-box;
    min-height: 0;
    overflow: visible;
    padding-bottom: 7px;
    border-radius: inherit;
  }

  .node-header {
    align-items: center;
    border-bottom: 1px solid rgba(63, 99, 243, 0.1);
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    min-height: 54px;
    padding: 9px 12px 8px;
    border-radius: 8px 8px 0 0;
  }

  .title-block {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    width: 100%;
  }

  .title {
    color: var(--color-app-text, var(--color-node-title));
    display: block;
    font-size: 13px;
    font-weight: 650;
    line-height: 1.2;
    margin: 0;
    min-width: 0;
    overflow: hidden;
    padding: 0;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .task-label {
    color: var(--color-app-text-secondary, var(--color-node-text));
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    pointer-events: none;
    text-overflow: ellipsis;
    text-align: center;
    white-space: nowrap;
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    margin: 10px auto 8px;
    color: var(--color-sidebar-selected-border, #3f63f3);
  }

  .icon svg {
    max-height: 30px;
    max-width: 30px;
    height: auto;
    width: auto;
    opacity: 0.92;
  }

  .input,
  .output {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    margin: 4px 0;
    padding: 0 var(--node-inline-padding);
    font-size: 12px;
    position: relative;
    min-height: 28px;
  }

  .input {
    flex-direction: row;
    justify-content: flex-start;
  }

  .output {
    flex-direction: row;
    justify-content: flex-end;
    text-align: right;
  }

  .input-socket-container {
    position: relative;
    width: var(--socket-size);
    height: var(--socket-size);
    flex-shrink: 0;
    margin-right: var(--socket-margin);
    margin-left: calc(
      -1 * (var(--node-inline-padding) + (var(--socket-size) / 2))
    );
    z-index: 1;
  }

  .output-socket-container {
    position: relative;
    width: var(--socket-size);
    height: var(--socket-size);
    flex-shrink: 0;
    margin-left: var(--socket-margin);
    margin-right: calc(
      -1 * (var(--node-inline-padding) + (var(--socket-size) / 2))
    );
    z-index: 1;
  }

  .input-title,
  .output-title {
    color: var(--color-app-text-secondary, var(--color-node-text));
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .input-title {
    padding-right: 4px;
  }

  .output-title {
    padding-left: 4px;
  }

  .output-value,
  .input-value {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    white-space: nowrap;
    max-width: 124px;
    overflow: hidden;
    color: var(--color-app-text-secondary, var(--color-node-value));
    font-size: 10px;
    font-weight: 500;
    line-height: 1.2;
    opacity: 0.7;
    pointer-events: none;
    text-overflow: ellipsis;
  }

  .value {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .value:empty {
    display: none;
  }

  .output-value {
    left: calc(100% + 6px);
  }

  .input-value {
    right: calc(100% + 10px);
  }

  .control {
    margin: 4px 0;
  }

  .error-message {
    color: var(--color-error-text, var(--color-node-error-message));
    font-size: 11px;
    font-weight: 650;
    line-height: 1.2;
    margin: 0;
    max-width: calc(100% + 24px);
    overflow-wrap: break-word;
    padding: 0;
    position: absolute;
    left: 8px;
    top: calc(100% + 6px);
  }
`;
